import React, { useState, useEffect } from "react";
import axios from "axios";
import { MapPin, Bell, BellOff, CheckCircle2, AlertTriangle, RefreshCw, X, Send } from "lucide-react";
import { getCurrentLocation } from "../../utils/maps";
import { subscribeToPushNotifications, unsubscribeFromPushNotifications, useGeolocation } from "../../App";
import { InboxSettingsBlock } from "../../components/notifications/InboxSettingsBlock";
import { PushHistoryBlock } from "../../components/notifications/PushHistoryBlock";
import { formatMsk } from "../../utils/timeUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NotificationSettings = ({ user, showToast, onNavigate }) => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { location, error, loading: locationLoading, currentStep, getCurrentLocation, cancelLocationRequest, setManualLocation } = useGeolocation();
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [pendingEnableNotifications, setPendingEnableNotifications] = useState(false);
  const [locationUpdateError, setLocationUpdateError] = useState(null);

  // Telegram state
  const [tgStatus, setTgStatus] = useState(null); // null = loading, {connected, ...}
  const [tgLinkData, setTgLinkData] = useState(null); // {deeplink, expires_in_minutes}
  const [tgLoading, setTgLoading] = useState(false);
  const [tgInstructionsOpen, setTgInstructionsOpen] = useState(false);

  useEffect(() => {
    checkNotificationSupport();
    loadSubscriptions();
    loadPushStatus();
    if (user && user.role === 'executor') {
      loadTelegramStatus();
    }
  }, []);

  const loadPushStatus = async () => {
    try {
      const response = await axios.get(`${API}/notifications/push-status`);
      setPushSubscribed(response.data.subscribed);
    } catch (e) {}
  };

  // ─── Telegram helpers ─────────────────────────────────────────────────────
  const loadTelegramStatus = async () => {
    try {
      const res = await axios.get(`${API}/telegram/status`);
      setTgStatus(res.data);
    } catch (e) {
      setTgStatus({ connected: false });
    }
  };

  const handleTelegramConnect = async () => {
    setTgLoading(true);
    try {
      const res = await axios.post(`${API}/telegram/generate-link`);
      setTgLinkData(res.data);
      setTgInstructionsOpen(true);
    } catch (e) {
      const msg = e.response?.data?.detail || "Ошибка при создании ссылки";
      showToast(msg, "error");
    } finally {
      setTgLoading(false);
    }
  };

  const handleTelegramDisconnect = async () => {
    setTgLoading(true);
    try {
      await axios.delete(`${API}/telegram/disconnect`);
      setTgStatus({ connected: false });
      setTgLinkData(null);
      showToast("Telegram отключён", "success");
    } catch (e) {
      showToast("Ошибка при отключении", "error");
    } finally {
      setTgLoading(false);
    }
  };

  const handleTelegramLinkOpened = () => {
    // Poll status after user opens the bot link
    setTimeout(async () => {
      await loadTelegramStatus();
    }, 5000);
  };

  const checkNotificationSupport = () => {
    const supported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
    if (!supported) {
      showToast("Ваш браузер не поддерживает push уведомления", "error");
    }
  };

  // Обертка для getCurrentLocation с обработкой ошибок уведомлений  
  const handleUpdateLocation = () => {
    setLocationUpdateError(null); // Сбрасываем предыдущие ошибки
    showToast("Определяем ваше местоположение...", "info");
    
    // Просто вызываем получение местоположения - 
    // обновление уведомлений происходит автоматически в useGeolocation после успешного определения
    getCurrentLocation();
  };

  const loadSubscriptions = async () => {
    try {
      const response = await axios.get(`${API}/notifications/my-subscriptions`);
      setSubscriptions(response.data);
      setNotificationsEnabled(response.data.length > 0);
    } catch (error) {
      console.error("Failed to load subscriptions:", error);
    }
  };

  const enableNotifications = async () => {
    // Если нет местоположения, сначала получаем его
    if (!location) {
      showToast("Определяем ваше местоположение для настройки уведомлений...", "info");
      setPendingEnableNotifications(true);
      getCurrentLocation();
      return;
    }

    if (error && !location) {
      showToast("Сначала необходимо исправить ошибку геолокации или ввести координаты вручную", "error");
      setPendingEnableNotifications(false);
      return;
    }

    setLoading(true);
    try {
      // 1. Geo-based subscription (old)
      const response = await axios.post(`${API}/notifications/subscribe`, {
        endpoint: `geo://user/${Date.now()}`,
        p256dh_key: "geo",
        auth_key: "geo",
        latitude: location.latitude,
        longitude: location.longitude,
        radius_km: 250
      });
      if (response.data) {
        setNotificationsEnabled(true);
        loadSubscriptions();
      }

      // 2. Real push subscription (new)
      const pushSuccess = await subscribeToPushNotifications();
      if (pushSuccess) {
        setPushSubscribed(true);
        showToast("Уведомления включены! Вы будете получать уведомления о событиях.", "success");
      } else {
        showToast("Гео-уведомления включены, но push-уведомления потребуют разрешения браузера.", "info");
      }
    } catch (err) {
      showToast("Ошибка при включении уведомлений", "error");
    } finally {
      setLoading(false);
    }
  };

  // Автоматически включаем уведомления, когда получаем местоположение (если пользователь пытался включить)
  useEffect(() => {
    if (location && pendingEnableNotifications && !notificationsEnabled && !loading) {
      setPendingEnableNotifications(false);
      // Небольшая задержка, чтобы пользователь увидел что местоположение определено
      setTimeout(() => {
        enableNotifications();
      }, 1000);
    }
    // Сбрасываем флаг при ошибке геолокации
    else if (error && pendingEnableNotifications) {
      setPendingEnableNotifications(false);
    }
  }, [location, pendingEnableNotifications, error]);

  // Показываем сообщение когда местоположение успешно обновлено (вне процесса включения уведомлений)
  const [prevLocation, setPrevLocation] = useState(null);
  useEffect(() => {
    // Если местоположение изменилось и это не первоначальная загрузка, и не процесс включения уведомлений
    if (location && prevLocation && !pendingEnableNotifications && !loading) {
      showToast("Местоположение обновлено успешно!", "success");
    }
    setPrevLocation(location);
  }, [location]);

  const disableNotifications = async () => {
    setLoading(true);
    try {
      for (const subscription of subscriptions) {
        await axios.post(`${API}/notifications/unsubscribe`, { endpoint: subscription.endpoint });
      }
      // Also unsubscribe from push
      await unsubscribeFromPushNotifications();
      setNotificationsEnabled(false);
      setPushSubscribed(false);
      setSubscriptions([]);
      showToast("Уведомления успешно отключены", "success");
    } catch (error) {
      console.error("Error disabling notifications:", error);
      showToast("Ошибка при отключении уведомлений. Попробуйте ещё раз.", "error");
    } finally {
      setLoading(false);
    }
  };

  const testNotification = async () => {
    try {
      if (!pushSubscribed) {
        showToast("Сначала включите push-уведомления", "error");
        return;
      }
      await axios.post(`${API}/notifications/push-test`);
      showToast("Тестовое уведомление отправлено!", "success");
    } catch (error) {
      console.error("Test notification error:", error);
      showToast("Ошибка при отправке тестового уведомления", "error");
    }
  };

  const formatDate = (dateString) => formatMsk(dateString);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><Bell size={20} style={{color:'var(--rv-teal)'}}/>Настройки уведомлений</h2>

      {user?.role === "executor" && (
        <div className="px-4 py-3 rounded-xl text-sm" style={{background:'rgba(13,148,136,0.08)', border:'1px solid var(--rv-teal)', color:'#0F766E'}} data-testid="notifications-recommendation-banner">
          <strong>Рекомендуем включить все три типа уведомлений:</strong> Push-уведомления — чтобы мгновенно получать сообщения о событиях, Гео-уведомления — чтобы первыми узнавать о новых заданиях рядом с вами, Telegram-уведомления — чтобы ничего не пропустить, даже когда браузер закрыт. Исполнители с включёнными уведомлениями быстрее берут задания в работу.
        </div>
      )}

      {/* Push Notifications Block */}
      <div className="reversia-card p-6">
        <h3 className="text-base font-semibold mb-2 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><Bell size={15} style={{color:'var(--rv-teal)'}}/>Push-уведомления о событиях</h3>
        <p className="text-sm mb-4" style={{color:'var(--rv-text-muted)'}}>
          Получайте уведомления на устройство когда приложение закрыто: новые сообщения, изменения статуса задания, запросы доплаты.
        </p>
        <div className="flex items-center justify-between mb-4">
          <span className="px-2 py-1 rounded-full text-sm" style={{
            background: pushSubscribed ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)',
            color: pushSubscribed ? '#10b981' : 'var(--rv-text-muted)',
          }}>
            {pushSubscribed ? 'Подключены' : 'Не подключены'}
          </span>
        </div>
        <div className="flex flex-wrap gap-3">
          {!pushSubscribed ? (
            <button
              data-testid="enable-push-btn"
              onClick={async () => {
                setLoading(true);
                try {
                  const ok = await subscribeToPushNotifications();
                  if (ok) { setPushSubscribed(true); showToast("Push-уведомления включены!", "success"); }
                  else showToast("Не удалось включить push-уведомления. Проверьте разрешения браузера.", "error");
                } finally { setLoading(false); }
              }}
              disabled={loading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm"
              style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer', opacity: loading ? 0.5 : 1}}
            >
              <Bell size={14}/>{loading ? "Подключение..." : "Включить push-уведомления"}
            </button>
          ) : (
            <button
              data-testid="disable-push-btn"
              onClick={async () => {
                setLoading(true);
                try {
                  await unsubscribeFromPushNotifications();
                  setPushSubscribed(false);
                  showToast("Push-уведомления отключены", "success");
                } finally { setLoading(false); }
              }}
              disabled={loading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm"
              style={{background:'var(--rv-error)', color:'white', border:'none', cursor:'pointer', opacity: loading ? 0.5 : 1}}
            >
              <BellOff size={14}/>Отключить push-уведомления
            </button>
          )}
          {pushSubscribed && (
            <button
              data-testid="test-push-btn"
              onClick={testNotification}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm"
              style={{background:'rgba(16,185,129,0.1)', color:'#10b981', border:'1px solid rgba(16,185,129,0.3)', cursor:'pointer'}}
            >
              <CheckCircle2 size={14}/>Тест уведомления
            </button>
          )}
        </div>
        <p className="text-xs mt-3" style={{color:'var(--rv-text-muted)'}}>
          Работает в Chrome, Firefox, Edge на Android и ПК. На iOS — только Safari 16.4+ при добавлении сайта на домашний экран.
        </p>
      </div>

      {/* Telegram Notifications Block (executors only) */}
      {user && user.role === 'executor' && (
        <div className="reversia-card p-6">
          <h3 className="text-base font-semibold mb-2 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>
            <Send size={15} style={{color:'var(--rv-teal)'}}/>Telegram-уведомления
          </h3>
          <p className="text-sm mb-2" style={{color:'var(--rv-text-muted)'}}>
            Получайте мгновенные уведомления о новых заданиях прямо в Telegram — даже если приложение закрыто. Уведомления приходят только о заданиях в вашем радиусе.
          </p>
          {onNavigate && (
            <button
              data-testid="tg-how-it-works-link"
              onClick={() => {
                localStorage.setItem('helpInitTab', 'executor');
                localStorage.setItem('helpScrollTo', 'telegram-notifications-instruction');
                onNavigate('help');
              }}
              className="text-sm mb-4"
              style={{color:'var(--rv-teal)', background:'none', border:'none', cursor:'pointer', padding:0, display:'inline', textDecoration:'underline'}}
            >
              Как это работает?
            </button>
          )}

          {/* Connection status */}
          <div className="flex items-center justify-between mb-4">
            <span className="px-2 py-1 rounded-full text-sm" style={{
              background: tgStatus?.connected ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)',
              color: tgStatus?.connected ? '#10b981' : 'var(--rv-text-muted)',
            }}>
              {tgStatus === null ? 'Загрузка...' : tgStatus.connected ? 'Подключён' : 'Не подключён'}
            </span>
            {tgStatus?.connected && tgStatus.radius_km && (
              <span className="text-xs" style={{color:'var(--rv-text-muted)'}}>Радиус: {tgStatus.radius_km} км</span>
            )}
          </div>

          {/* Connected info */}
          {tgStatus?.connected && (
            <div className="mb-4 p-3 rounded-lg" style={{background:'rgba(13,148,136,0.04)', border:'1px solid rgba(13,148,136,0.15)'}}>
              <p className="text-sm" style={{color:'var(--rv-teal)'}}>
                Telegram подключён{tgStatus.telegram_username ? ` (@${tgStatus.telegram_username})` : ''}
              </p>
              <p className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>
                Радиус поиска заданий: {tgStatus.radius_km} км
              </p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            {!tgStatus?.connected ? (
              <button
                data-testid="telegram-connect-btn"
                onClick={handleTelegramConnect}
                disabled={tgLoading}
                className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm"
                style={{background:'#229ED9', color:'white', border:'none', cursor:'pointer', opacity: tgLoading ? 0.5 : 1}}
              >
                <Send size={14}/>{tgLoading ? "Создание ссылки..." : "Подключить Telegram"}
              </button>
            ) : (
              <button
                data-testid="telegram-disconnect-btn"
                onClick={handleTelegramDisconnect}
                disabled={tgLoading}
                className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm"
                style={{background:'var(--rv-error)', color:'white', border:'none', cursor:'pointer', opacity: tgLoading ? 0.5 : 1}}
              >
                <BellOff size={14}/>{tgLoading ? "Отключение..." : "Отключить Telegram"}
              </button>
            )}
          </div>

          {/* Instructions modal */}
          {tgInstructionsOpen && tgLinkData && (
            <div className="mt-4 p-4 rounded-xl" style={{background:'rgba(34,158,217,0.06)', border:'1px solid rgba(34,158,217,0.25)'}}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold" style={{color:'var(--rv-text-primary)'}}>Инструкция по подключению</h4>
                <button onClick={() => setTgInstructionsOpen(false)} style={{background:'none', border:'none', cursor:'pointer', color:'var(--rv-text-muted)'}}>
                  <X size={16}/>
                </button>
              </div>

              <ol className="text-sm space-y-2 mb-4" style={{color:'var(--rv-text-primary)'}}>
                <li style={{display:'flex', gap:'8px'}}><span style={{color:'var(--rv-teal)', fontWeight:600, minWidth:'16px'}}>1.</span><span>Нажмите кнопку <b>"Открыть в Telegram"</b> ниже</span></li>
                <li style={{display:'flex', gap:'8px'}}><span style={{color:'var(--rv-teal)', fontWeight:600, minWidth:'16px'}}>2.</span><span>В открывшемся боте нажмите <b>Старт</b> (или Start)</span></li>
                <li style={{display:'flex', gap:'8px'}}><span style={{color:'var(--rv-teal)', fontWeight:600, minWidth:'16px'}}>3.</span><span>Бот попросит поделиться геолокацией — нажмите кнопку <b>"Поделиться геолокацией"</b></span></li>
                <li style={{display:'flex', gap:'8px'}}><span style={{color:'var(--rv-teal)', fontWeight:600, minWidth:'16px'}}>4.</span><span>Выберите <b>радиус поиска</b> заданий (например: 50 км)</span></li>
                <li style={{display:'flex', gap:'8px'}}><span style={{color:'var(--rv-teal)', fontWeight:600, minWidth:'16px'}}>5.</span><span>Готово! Telegram подключён. Вернитесь на эту страницу и обновите статус</span></li>
              </ol>

              <div className="text-xs mb-4 p-2 rounded" style={{background:'rgba(217,119,6,0.08)', color:'#92400e'}}>
                Ссылка действует {tgLinkData.expires_in_minutes} минут. После истечения нажмите "Подключить Telegram" снова.
              </div>

              <div className="flex flex-wrap gap-2">
                <a
                  data-testid="telegram-open-bot-link"
                  href={tgLinkData.deeplink_tg || tgLinkData.deeplink}
                  onClick={handleTelegramLinkOpened}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm no-underline"
                  style={{background:'#229ED9', color:'white', textDecoration:'none'}}
                >
                  <Send size={14}/>Открыть в Telegram
                </a>
                {tgLinkData.deeplink && (
                  <a
                    data-testid="telegram-open-bot-link-web"
                    href={tgLinkData.deeplink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleTelegramLinkOpened}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm no-underline"
                    style={{background:'rgba(34,158,217,0.15)', color:'#229ED9', border:'1px solid rgba(34,158,217,0.3)', textDecoration:'none'}}
                  >
                    <Send size={14}/>Открыть в браузере
                  </a>
                )}
                <button
                  onClick={loadTelegramStatus}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm"
                  style={{background:'rgba(13,148,136,0.1)', color:'var(--rv-teal)', border:'1px solid rgba(13,148,136,0.2)', cursor:'pointer'}}
                >
                  <RefreshCw size={14}/>Обновить статус
                </button>
              </div>
            </div>
          )}

          <p className="text-xs mt-3" style={{color:'var(--rv-text-muted)'}}>
            Работает на всех устройствах где установлен Telegram. Уведомления доступны исполнителям.
          </p>
        </div>
      )}

      {/* Geo Notifications Block */}
      <div className="reversia-card p-6">
        <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><MapPin size={15} style={{color:'var(--rv-teal)'}}/>Гео-уведомления</h3>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <p style={{color:'var(--rv-text-primary)'}}>
              Получайте уведомления о новых заданиях в радиусе 250 км от вашего местоположения
            </p>
            <p className="text-sm mt-1" style={{color:'var(--rv-text-muted)'}}>
              Уведомления работают только при включенной геолокации
            </p>
          </div>
          <div className="ml-4 flex-shrink-0">
            <span className="px-2 py-1 rounded-full text-sm" style={{
              background: notificationsEnabled ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)',
              color: notificationsEnabled ? '#10b981' : 'var(--rv-text-muted)',
            }}>
              {notificationsEnabled ? 'Включены' : 'Отключены'}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {!notificationsEnabled ? (
            <button
              onClick={enableNotifications}
              disabled={loading || locationLoading || (error && !location)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm"
              style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer', opacity: (loading || locationLoading) ? 0.5 : 1}}
            >
              <Bell size={14}/>{loading ? "Включение..." : 
               locationLoading ? "Определение местоположения..." :
               error && !location ? "Исправьте ошибку геолокации" :
               "Включить уведомления"}
            </button>
          ) : (
            <button
              onClick={disableNotifications}
              disabled={loading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm"
              style={{background:'var(--rv-error)', color:'white', border:'none', cursor:'pointer', opacity: loading ? 0.5 : 1}}
            >
              <BellOff size={14}/>{loading ? "Отключение..." : "Отключить уведомления"}
            </button>
          )}
          
          <button
            onClick={handleUpdateLocation}
            disabled={locationLoading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm"
            style={{background:'rgba(107,114,128,0.1)', color:'var(--rv-text-primary)', border:'1px solid var(--rv-border)', cursor:'pointer', opacity: locationLoading ? 0.5 : 1}}
          >
            <RefreshCw size={14}/>{locationLoading ? "Определение..." : "Обновить местоположение"}
          </button>

          {notificationsEnabled && pushSubscribed && (
            <button
              onClick={testNotification}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm"
              style={{background:'rgba(16,185,129,0.1)', color:'#10b981', border:'1px solid rgba(16,185,129,0.3)', cursor:'pointer'}}
            >
              <CheckCircle2 size={14}/>Тест уведомления
            </button>
          )}
        </div>

        {/* Location Update Error */}
        {locationUpdateError && (
          <div className="mt-4 p-3 rounded-md" style={{background:'rgba(217,119,6,0.06)', border:'1px solid rgba(217,119,6,0.2)'}}>
            <div className="flex items-start gap-2">
              <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" style={{color:'#d97706'}}/>
              <div>
                <h4 className="text-sm font-medium" style={{color:'#92400e'}}>Предупреждение</h4>
                <p className="text-sm mt-0.5" style={{color:'#b45309'}}>{locationUpdateError}</p>
                <p className="text-xs mt-0.5" style={{color:'#d97706'}}>
                  Местоположение определено успешно, но обновление настроек уведомлений не удалось.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Location Info */}
        {location && (
          <div className="mt-4 p-3 rounded-md" style={{background:'rgba(13,148,136,0.04)', border:'1px solid rgba(13,148,136,0.1)'}}>
            <p className="text-sm flex items-center gap-1.5" style={{color:'var(--rv-teal)'}}>
              <MapPin size={13}/>Текущее местоположение: {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
            </p>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 rounded-md" style={{background:'rgba(239,68,68,0.04)', border:'1px solid rgba(239,68,68,0.2)'}}>
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" style={{color:'#ef4444'}}/>
              <div>
                <h3 className="text-sm font-medium" style={{color:'#b91c1c'}}>Ошибка геолокации</h3>
                <p className="text-sm mt-1" style={{color:'#ef4444'}}>{error}</p>
                <div className="mt-3">
                  <button
                    onClick={getCurrentLocation}
                    disabled={locationLoading}
                    className="flex items-center gap-1.5 px-3 py-1 rounded text-sm"
                    style={{background:'var(--rv-error)', color:'white', border:'none', cursor:'pointer', opacity: locationLoading ? 0.5 : 1}}
                  >
                    <RefreshCw size={12}/>{locationLoading ? "Попытка..." : "Попробовать снова"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {locationLoading && (
          <div className="mt-4 p-3 rounded-md" style={{background:'rgba(13,148,136,0.04)', border:'1px solid rgba(13,148,136,0.1)'}}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{borderColor:'var(--rv-teal)'}}></div>
                <p className="text-sm" style={{color:'var(--rv-teal)'}}>{currentStep || 'Определяем ваше местоположение...'}</p>
              </div>
              <button
                onClick={() => {
                  cancelLocationRequest();
                  setPendingEnableNotifications(false);
                }}
                className="flex items-center gap-1 px-2 py-1 rounded text-xs"
                style={{background:'var(--rv-error)', color:'white', border:'none', cursor:'pointer'}}
              >
                <X size={11}/>Отменить
              </button>
            </div>
            <div className="mt-2 text-xs" style={{color:'var(--rv-text-muted)'}}>
              <p>GPS (высокая точность) → Сеть (стандартная) → IP-геолокация</p>
            </div>
          </div>
        )}

        {/* Manual coordinates input */}
        {(error || showManualInput) && (
          <div className="mt-4 p-4 rounded-md" style={{background:'rgba(217,119,6,0.04)', border:'1px solid rgba(217,119,6,0.15)'}}>
            <h4 className="text-sm font-medium mb-3" style={{color:'#92400e'}}>
              {error ? "Ручной ввод координат (альтернатива)" : "Ввод координат вручную"}
            </h4>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{color:'var(--rv-text-muted)'}}>Широта</label>
                  <input
                    type="number"
                    step="any"
                    value={manualLat}
                    onChange={(e) => setManualLat(e.target.value)}
                    placeholder="55.7558"
                    className="w-full px-2 py-1 rounded text-sm"
                    style={{border:'1px solid var(--rv-border)', outline:'none'}}
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{color:'var(--rv-text-muted)'}}>Долгота</label>
                  <input
                    type="number"
                    step="any"
                    value={manualLng}
                    onChange={(e) => setManualLng(e.target.value)}
                    placeholder="37.6173"
                    className="w-full px-2 py-1 rounded text-sm"
                    style={{border:'1px solid var(--rv-border)', outline:'none'}}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (manualLat && manualLng) {
                      setManualLocation(manualLat, manualLng);
                      setShowManualInput(false);
                    }
                  }}
                  disabled={!manualLat || !manualLng}
                  className="flex items-center gap-1 px-3 py-1 rounded text-sm"
                  style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer'}}
                >
                  <MapPin size={12}/>Использовать эти координаты
                </button>
                <button
                  onClick={() => setShowManualInput(false)}
                  className="px-3 py-1 rounded text-sm"
                  style={{background:'rgba(107,114,128,0.1)', color:'var(--rv-text-muted)', border:'none', cursor:'pointer'}}
                >
                  Скрыть
                </button>
              </div>
              <div className="text-xs" style={{color:'var(--rv-text-muted)'}}>
                <p>Найдите координаты на maps.google.com или maps.yandex.ru</p>
                <p>Москва: 55.7558, 37.6173 | СПб: 59.9311, 30.3609</p>
              </div>
            </div>
          </div>
        )}
        
        {!showManualInput && !locationLoading && (
          <div className="mt-4">
            <button
              onClick={() => setShowManualInput(true)}
              className="text-sm underline"
              style={{color:'var(--rv-teal)', background:'none', border:'none', cursor:'pointer'}}
            >
              Ввести координаты вручную
            </button>
          </div>
        )}
      </div>

      {/* Active Subscriptions */}
      {subscriptions.length > 0 && (
        <div className="reversia-card p-6">
          <h3 className="text-base font-semibold mb-4" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>Активные подписки</h3>
          <div className="space-y-3">
            {subscriptions.map((subscription, index) => (
              <div key={subscription.id} className="p-3 rounded-lg" style={{border:'1px solid var(--rv-border)'}}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium" style={{color:'var(--rv-text-primary)'}}>Подписка #{index + 1}</p>
                    <p className="text-xs mt-0.5" style={{color:'var(--rv-text-muted)'}}>
                      Радиус: {subscription.radius_km} км
                    </p>
                    <p className="text-xs" style={{color:'var(--rv-text-muted)'}}>
                      Местоположение: {subscription.latitude.toFixed(4)}, {subscription.longitude.toFixed(4)}
                    </p>
                    <p className="text-xs" style={{color:'var(--rv-text-muted)'}}>
                      Создана: {formatDate(subscription.created_at)}
                    </p>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full" style={{background:'rgba(16,185,129,0.1)', color:'#10b981'}}>
                    Активна
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <InboxSettingsBlock onOpenTask={(taskId) => { try { sessionStorage.setItem("rv_focus_task", taskId); } catch (e) {} onNavigate && onNavigate("my-tasks"); }} />

      <PushHistoryBlock />

      {/* Browser Support Info */}
      <div className="reversia-card p-4" style={{background:'rgba(13,148,136,0.04)', borderColor:'rgba(13,148,136,0.15)'}}>
        <h4 className="font-medium mb-2 text-sm" style={{color:'var(--rv-text-primary)'}}>Информация о поддержке</h4>
        <ul className="text-sm space-y-0.5" style={{color:'var(--rv-text-muted)'}}>
          <li>• Уведомления работают в современных браузерах (Chrome, Firefox, Safari)</li>
          <li>• На мобильных устройствах уведомления работают даже когда приложение закрыто</li>
          <li>• Для работы необходимо разрешение на отправку уведомлений и доступ к геолокации</li>
          <li>• Уведомления отправляются только при наличии новых заданий в радиусе 250 км</li>
        </ul>
      </div>
    </div>
  );
};

